<template>
  <b-modal
    v-model="showModal"
    title="Update POB"
    size="lg"
    class="m-4"
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
    @ok="submitHandler"
  >
    <b-form
      @submit.prevent="submitHandler"
    >
      <b-row>
        <b-col cols="12">
          <b-alert
            variant="danger"
            :show="errorMessage ? true : false"
          >
            <div
              v-if="typeof errorMessage === 'string'"
              class="alert-body"
            >
              <p>
                {{ errorMessage }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.detail"
              class="alert-body"
            >
              <p>
                {{ errorMessage.detail }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.non_field_errors"
              class="alert-body"
            >
              <p>
                {{ errorMessage.non_field_errors[0] }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.message"
              class="alert-body"
            >
              <p>
                {{ errorMessage.message }}
              </p>
            </div>
          </b-alert>
        </b-col>

        <!-- Basic Information -->
        <b-col cols="6">
          <b-form-group
            label-for="company"
          >
            <template #label>
              Company
              <span
                v-if="isFieldRequired('company')"
                class="text-danger"
              >*</span>
            </template>
            <custom-v-select
              id="company"
              v-model="selectedCompany"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="companyOptions"
              :class="{ 'error-border': validationErrors.company }"
              label="text"
              @input="handleCompanyChange"
            />
            <p
              v-if="validationErrors.company"
              class="error-message"
            >
              {{ validationErrors.company }}
            </p>
            <p
              v-if="errorMessage && errorMessage.company"
              class="error-message"
            >
              {{ errorMessage.company[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label-for="mr"
          >
            <template #label>
              MR
              <span
                v-if="isFieldRequired('mr')"
                class="text-danger"
              >*</span>
            </template>
            <custom-v-select
              id="mr"
              v-model="selectedMr"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="mrOptions"
              :loading="mrLoading"
              :disabled="!selectedCompany"
              :class="{ 'error-border': validationErrors.mr }"
              label="text"
              multiple
              placeholder="Select MR"
              @open="handleMrFocus"
              @input="handleMrChange"
            />
            <p
              v-if="validationErrors.mr"
              class="error-message"
            >
              {{ validationErrors.mr }}
            </p>
            <p
              v-if="mrLoadingError"
              class="error-message"
            >
              {{ mrLoadingError }}
            </p>
            <p
              v-if="errorMessage && errorMessage.mr"
              class="error-message"
            >
              {{ errorMessage.mr[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label-for="party"
          >
            <template #label>
              Party
              <span
                v-if="isFieldRequired('party')"
                class="text-danger"
              >*</span>
            </template>
            <custom-v-select
              id="party"
              v-model="selectedParty"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="partyOptions"
              :class="{ 'error-border': validationErrors.party }"
              label="text"
              @input="handlePartyChange"
            />
            <p
              v-if="validationErrors.party"
              class="error-message"
            >
              {{ validationErrors.party }}
            </p>
            <p
              v-if="errorMessage && errorMessage.party"
              class="error-message"
            >
              {{ errorMessage.party[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            :label="'Mode Of Giving POB' + (isFieldRequired('pob_mode') ? ' *' : '')"
            label-for="pobMode"
          >
            <custom-v-select
              id="pobMode"
              v-model="selectedPobMode"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="pobModeOptions"
              :class="{ 'error-border': validationErrors.pob_mode }"
              label="text"
              @input="handlePobModeChange"
            />
            <p
              v-if="validationErrors.pob_mode"
              class="error-message"
            >
              {{ validationErrors.pob_mode }}
            </p>
            <p
              v-if="errorMessage && errorMessage.pob_mode"
              class="error-message"
            >
              {{ errorMessage.pob_mode[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            :label="'Scan Of Paper (PDF or Image file)' + (isFieldRequired('pob_file') ? ' *' : '')"
            label-for="pobFile"
          >
            <!-- Show existing file if available -->
            <div
              v-if="existingFileUrl && !pobFile"
              class="mb-2"
            >
              <div class="d-flex align-items-center">
                <span class="text-muted mr-2">Current file:</span>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="viewFile"
                >
                  <feather-icon
                    icon="EyeIcon"
                    size="14"
                    class="mr-1"
                  />
                  View File
                </b-button>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="ml-2"
                  @click="removeExistingFile"
                >
                  <feather-icon
                    icon="XIcon"
                    size="14"
                    class="mr-1"
                  />
                  Remove
                </b-button>
              </div>
            </div>

            <b-form-file
              id="pobFile"
              v-model="pobFile"
              :class="{ 'error-border': validationErrors.pob_file }"
              accept=".pdf,.doc,.docx,.jpg,.png,.jpeg"
              :placeholder="existingFileUrl && !pobFile ? 'Choose new file to replace...' : 'Choose POB file...'"
            />
            <p
              v-if="validationErrors.pob_file"
              class="error-message"
            >
              {{ validationErrors.pob_file }}
            </p>
            <p
              v-if="errorMessage && errorMessage.pob_file"
              class="error-message"
            >
              {{ errorMessage.pob_file[0] }}
            </p>
          </b-form-group>
        </b-col>

        <!-- POB Items Section -->
        <b-col cols="12">
          <h5 class="mt-1 mb-2">
            POB Items <span class="text-danger">*</span>
          </h5>
          <div
            v-for="(item, index) in pobItems"
            :key="`item-${item.tempId || item.id || index}`"
          >
            <b-row>
              <b-col
                cols="6"
                class="d-flex align-items-center justify-content-center"
              >
                <span
                  class="h3 mx-1"
                >{{ index + 1 }}.</span>

                <b-form-group
                  :label-for="`product-${index+1}`"
                  class="w-100"
                >
                  <custom-v-select
                    :id="`product-${index}`"
                    v-model="item.product"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="item.filteredProductOptions || []"
                    :loading="productOptionsLoading"
                    :disabled="!selectedCompany"
                    :class="{ 'error-border': validationErrors[`product_${index}`] || item.isDuplicate }"
                    label="display_text"
                    placeholder="Search product..."
                    :filterable="false"
                    @search="(query) => searchProducts(query, index)"
                    @input="(product) => handleProductChange(product, index)"
                    @open="() => onProductDropdownOpen(index)"
                  />
                  <!-- Show validation error for this specific item -->
                  <p
                    v-if="validationErrors[`product_${index}`]"
                    class="error-message"
                  >
                    {{ validationErrors[`product_${index}`] }}
                  </p>
                  <!-- Show duplicate error for this specific item -->
                  <p
                    v-if="item.isDuplicate"
                    class="error-message"
                  >
                    This item is already selected in another row
                  </p>
                </b-form-group>
              </b-col>
              <b-col
                cols="2"
                class="d-flex align-items-center justify-content-center"
              >
                <span
                  v-if="item.rate > 0"
                  class="h5 mx-1"
                >  {{ item.rate }} ₹</span>
                <span
                  v-else
                  class="h5 mx-1"
                >  0</span>
              </b-col>
              <b-col cols="3">
                <b-form-group>
                  <div class="d-flex gap-2">
                    <b-form-input
                      :id="`quantity-${index}`"
                      v-model="item.quantity"
                      type="number"
                      min="0"
                      :class="{ 'error-border': validationErrors[`quantity_${index}`] }"
                      class="mr-1"
                      placeholder="Qty"
                    />
                    <p
                      class="h2 mr-1"
                      style="margin-top: 5px;"
                    >
                      +
                    </p>
                    <b-form-input
                      :id="`free-quantity-${index}`"
                      v-model="item.free_quantity"
                      type="number"
                      min="0"
                      placeholder="Free"
                    />
                  </div>
                  <p
                    v-if="validationErrors[`quantity_${index}`]"
                    class="error-message"
                  >
                    {{ validationErrors[`quantity_${index}`] }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col
                cols="1"
                class="d-flex align-items-center justify-content-center"
                style="padding-bottom: 10px;"
              >
                <feather-icon
                  v-if="pobItems.length > 1"
                  title="Delete"
                  icon="TrashIcon"
                  size="25"
                  color="red"
                  class="cursor-pointer"
                  @click="removeItem(index)"
                />
              </b-col>
            </b-row>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <b-button
              variant="outline-primary"
              :disabled="!selectedCompany"
              @click="addItem"
            >
              <feather-icon
                icon="PlusIcon"
                size="16"
                class="mr-1"
              />
              Add Item
            </b-button>

            <div class="total-order-value">
              <h5 class="mb-0 text-primary">
                Total Order Value: <strong> ₹ {{ totalOrderValue.toFixed(2) }}</strong>
              </h5>
            </div>
          </div>
        </b-col>

        <!-- POB Remarks Section -->
        <b-col cols="12">
          <h5 class="mt-2">
            POB Remarks
          </h5>

          <!-- Existing Remarks (Read-only) -->
          <div
            v-for="(remark) in existingRemarks"
            :key="`existing-remark-${remark.id}`"
            class="mb-1"
          >
            <div class="existing-remark-card">
              <div class="d-flex justify-content-between align-items-start">
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center mb-1">
                    <b-badge
                      variant="primary"
                      class="mr-2"
                    >
                      {{ remark.created_by_fullname }}
                    </b-badge>
                    <small class="text-muted">
                      {{ formatDate(remark.created_at) }}
                    </small>
                  </div>
                  <p class="mb-0 remark-text">
                    {{ remark.remarks }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- New Remarks (Editable) -->
          <div
            v-for="(remark, index) in newRemarks"
            :key="`new-remark-${remark.tempId || index}`"
          >
            <b-row>
              <span
                class="h3 ml-2"
              >{{ existingRemarks.length + index + 1 }}.</span>
              <b-col cols="10">
                <b-form-group
                  :label-for="`remarks-${index+1}`"
                >
                  <b-form-textarea
                    :id="`remarks-${index}`"
                    v-model="remark.remarks"
                    rows="2"
                    placeholder="Enter new remarks"
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="1"
                class="d-flex align-items-center justify-content-center"
                style="padding-bottom: 15px;"
              >
                <feather-icon
                  v-if="newRemarks.length > 1"
                  title="Delete"
                  icon="TrashIcon"
                  size="25"
                  color="red"
                  class="cursor-pointer"
                  @click="removeNewRemark(index)"
                />
              </b-col>
            </b-row>
          </div>

          <b-button
            variant="outline-primary"
            @click="addNewRemark"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-1"
            />
            Add New Remark
          </b-button>
        </b-col>

      </b-row>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="saving"
        @click="ok()"
      >
        Update POB
        <b-spinner
          v-if="saving"
          small
          label="Small Spinner"
        />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BButton, BFormInput,
  BForm, BSpinner, BAlert, BFormTextarea, BFormFile, BBadge,
} from 'bootstrap-vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import getParsedQueryString from '@/utils/headerQueryParser'

export default {
  components: {
    CustomVSelect,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BSpinner,
    BAlert,
    BFormTextarea,
    BFormFile,
    BBadge,
  },

  props: {
    id: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      showModal: true,
      selectedCompany: null,
      companyOptions: [],
      selectedMr: null,
      mrOptions: [],
      mrLoading: false,
      mrLoadingError: null,
      selectedParty: null,
      partyOptions: [],
      selectedPobMode: { value: 'call', text: 'Call' }, // Default
      pobModeOptions: [
        { value: 'call', text: 'Call' },
        { value: 'paper', text: 'Paper' },
        { value: 'whatsapp', text: 'WhatsApp' },
      ],
      placedAtDate: null,
      pobFile: null,
      existingFileUrl: null,
      productOptions: [], // Global product options loaded once
      productOptionsLoading: false,
      pobItems: [],
      existingRemarks: [], // Read-only existing remarks
      newRemarks: [], // Editable new remarks
      saving: false,
      errorMessage: {},
      validationErrors: {},
      searchTimeouts: {}, // To handle debouncing for each item
      tempIdCounter: 0,
      isInitialLoad: true, // Flag to track initial data loading
      // Define mandatory fields
      mandatoryFields: ['company', 'mr', 'party', 'pob_mode'],
    }
  },

  computed: {
    totalOrderValue() {
      return this.pobItems.reduce((total, item) => {
        const quantity = parseFloat(item.quantity) || 0
        const rate = parseFloat(item.rate) || 0
        return total + (quantity * rate)
      }, 0)
    },
  },

  async created() {
    // First fetch basic options (company and party)
    await this.fetchBasicOptions()
    // Then fetch POB details and related options based on company
    await this.fetchPobDetails()
  },

  beforeDestroy() {
    // Clear all timeouts when component is destroyed
    Object.values(this.searchTimeouts).forEach(timeout => {
      clearTimeout(timeout)
    })
  },

  methods: {
    isFieldRequired(fieldName) {
      return this.mandatoryFields.includes(fieldName)
    },

    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    generateTempId() {
      this.tempIdCounter += 1
      return `temp_${this.tempIdCounter}_${Date.now()}`
    },

    validateForm() {
      this.validationErrors = {}
      let isValid = true

      // Validate mandatory fields
      if (!this.selectedCompany) {
        this.validationErrors.company = 'Company is required'
        isValid = false
      }

      if (!this.selectedMr || this.selectedMr.length === 0) {
        this.validationErrors.mr = 'MR is required'
        isValid = false
      }

      if (!this.selectedParty) {
        this.validationErrors.party = 'Party is required'
        isValid = false
      }

      if (!this.selectedPobMode) {
        this.validationErrors.pob_mode = 'Mode of giving POB is required'
        isValid = false
      }

      // Validate POB Items (at least one item with product and quantity)
      const validItems = this.pobItems.filter(item => item.product && (parseFloat(item.quantity) > 0 || parseFloat(item.free_quantity) > 0))

      if (validItems.length === 0) {
        this.validationErrors.pob_items = 'At least one POB item with product and quantity is required'
        isValid = false
      }

      // Validate individual items
      this.pobItems.forEach((item, index) => {
        if (item.product && !item.quantity && !item.free_quantity) {
          this.validationErrors[`quantity_${index}`] = 'Quantity is required when product is selected'
          isValid = false
        }

        if ((item.quantity || item.free_quantity) && !item.product) {
          this.validationErrors[`product_${index}`] = 'Product is required when quantity is provided'
          isValid = false
        }
      })

      // Check for duplicates
      this.checkForDuplicates()
      const hasDuplicates = this.pobItems.some(item => item.isDuplicate)
      if (hasDuplicates) {
        isValid = false
      }

      return isValid
    },

    async fetchBasicOptions() {
      try {
        // Fetch company options
        const companyRes = await axios.get('/company_options')
        this.companyOptions = companyRes.data.data

        // Fetch party options
        const partyRes = await axios.get('/accounts/buyers_options')
        this.partyOptions = partyRes.data.data
      } catch (error) {
        this.errorMessage = error?.response?.data || 'Failed to fetch basic options'
      }
    },

    async fetchProductOptions() {
      this.productOptionsLoading = true
      try {
        const productRes = await axios.get('/marg/product_options')
        this.productOptions = productRes.data.data.map(product => ({
          id: product.code,
          code: product.code,
          name: product.name,
          display_text: `${product.name}`,
          rate: parseFloat(product.rate),
          mrp: parseFloat(product.mrp),
        }))

        // Initialize all existing items with first 15 products for better performance
        this.pobItems.forEach((item, index) => {
          this.$set(this.pobItems[index], 'filteredProductOptions', this.productOptions.slice(0, 15))
        })

        this.productOptionsLoading = false
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || 'Failed to load products'
        this.productOptionsLoading = false
      }
    },

    // Handle product dropdown open - show initial products immediately
    onProductDropdownOpen(itemIndex) {
      const item = this.pobItems[itemIndex]
      if (!item.filteredProductOptions || item.filteredProductOptions.length === 0) {
        this.$set(this.pobItems[itemIndex], 'filteredProductOptions', this.productOptions.slice(0, 15))
      }
    },

    async fetchPobDetails() {
      try {
        const res = await axios.get(`/marg/pobs/${this.id}/`)
        const { data } = res

        // Pre-populate basic fields
        if (data.company) {
          // Find company in options or create it
          let company = this.companyOptions.find(c => c.id === data.company)
          if (!company) {
            company = { id: data.company, text: data.company_name }
            this.companyOptions.push(company)
          }
          this.selectedCompany = company
        }

        if (data.party) {
          // Find party in options or create it
          let party = this.partyOptions.find(p => p.id === data.party)
          if (!party) {
            party = { id: data.party, text: data.party_name }
            this.partyOptions.push(party)
          }
          this.selectedParty = party
        }

        if (data.pob_mode) {
          this.selectedPobMode = this.pobModeOptions.find(m => m.value === data.pob_mode)
            || { value: data.pob_mode, text: data.pob_mode.charAt(0).toUpperCase() + data.pob_mode.slice(1) }
        }

        if (data.placed_at) {
          this.placedAtDate = data.placed_at
        }

        // Handle file
        if (data.pob_file) {
          this.existingFileUrl = data.pob_file
        }

        // Fetch product options once
        await this.fetchProductOptions()

        // Now fetch company-dependent options and populate data
        if (data.company) {
          // Fetch MR options first, then set selection
          await this.fetchMrOptions(data.company)

          // Set MR selection after options are loaded
          if (data.mr && data.mr.length > 0) {
            this.selectedMr = this.mrOptions.filter(option => data.mr?.map(item => item.id).includes(option.id))
          }
        }

        // Pre-populate POB Items
        if (data.pob_items && data.pob_items.length > 0) {
          this.pobItems = data.pob_items.map(item => ({
            id: item.id,
            tempId: null,
            product: {
              id: item.code,
              code: item.code,
              name: item.product,
              display_text: item.product,
              rate: parseFloat(item.rate) || 0,
              mrp: parseFloat(item.mrp) || 0,
            },
            quantity: item.quantity,
            rate: parseFloat(item.rate) || 0,
            mrp: parseFloat(item.mrp) || 0,
            free_quantity: item.free_quantity,
            approved_free_quantity: item.approved_free_quantity,
            approved_quantity: item.approved_quantity,
            filteredProductOptions: this.productOptions.slice(0, 15),
            isDuplicate: false,
          }))
        } else {
          // Initialize with one empty item if no existing items
          this.initializeEmptyItems()
        }

        // Handle POB Remarks - separate existing and new
        if (data.pob_remarks && data.pob_remarks.length > 0) {
          this.existingRemarks = data.pob_remarks.map(remark => ({
            id: remark.id,
            remarks: remark.remarks,
            created_by: remark.created_by,
            created_by_fullname: remark.created_by_fullname,
            created_at: remark.created_at,
          }))
        } else {
          this.existingRemarks = []
        }

        // Initialize with one empty new remark
        this.initializeEmptyNewRemarks()

        this.isInitialLoad = false
      } catch (error) {
        this.errorMessage = error?.response?.data || 'Failed to fetch POB details'
      }
    },

    handleCompanyChange(company) {
      // Clear validation error immediately when company is selected
      if (this.validationErrors.company) {
        this.$delete(this.validationErrors, 'company')
      }

      // Don't reset data during initial load
      if (this.isInitialLoad) return

      // Reset MR selection when company changes
      this.selectedMr = null
      this.mrOptions = []
      this.mrLoadingError = null

      // Clear all search timeouts
      Object.values(this.searchTimeouts).forEach(timeout => {
        clearTimeout(timeout)
      })
      this.searchTimeouts = {}

      // Clear any existing error messages
      this.errorMessage = {}
      this.validationErrors = {}

      // Fetch new data for the selected company
      if (company && company.id) {
        this.fetchMrOptions(company.id)
      }
    },

    handleMrChange() {
      // Clear validation error immediately when MR is selected
      if (this.validationErrors.mr) {
        this.$delete(this.validationErrors, 'mr')
      }
    },

    handlePartyChange() {
      // Clear validation error immediately when party is selected
      if (this.validationErrors.party) {
        this.$delete(this.validationErrors, 'party')
      }
    },

    handlePobModeChange() {
      // Clear validation error immediately when pob mode is selected
      if (this.validationErrors.pob_mode) {
        this.$delete(this.validationErrors, 'pob_mode')
      }
    },

    async fetchMrOptions(companyId) {
      const params = { company: companyId, is_active: 'True' }

      this.mrLoading = true
      this.mrLoadingError = null

      try {
        const res = await axios.get(`/mr/?${getParsedQueryString(params)}`)
        this.mrOptions = res.data.results.map(item => {
          // Find primary mobile number
          const primaryMobile = item.mr_mobiles?.find(mobile => mobile.is_primary)?.mobile || ''

          // Create display text with name and primary mobile
          const displayText = primaryMobile
            ? `${item.name} ( ${primaryMobile} )`
            : item.name

          return {
            id: item.id,
            text: displayText,
            name: item.name,
            mobile: primaryMobile,
          }
        })
        if (this.mrOptions.length === 1 && !this.selectedMr) {
          this.selectedMr = [this.mrOptions[0]]
        }
        this.mrLoading = false
      } catch (error) {
        this.mrLoadingError = error?.response?.data?.error || 'Something went wrong'
        this.mrLoading = false
      }
    },

    initializeEmptyItems() {
      this.pobItems = [
        {
          id: null,
          tempId: this.generateTempId(),
          product: null,
          quantity: null,
          rate: null,
          mrp: null,
          free_quantity: null,
          filteredProductOptions: this.productOptions.slice(0, 15),
          isDuplicate: false,
        },
      ]
    },

    initializeEmptyNewRemarks() {
      this.newRemarks = [
        {
          id: null,
          tempId: this.generateTempId(),
          remarks: '',
        },
      ]
    },

    // Client-side product search function with optimized performance
    searchProducts(query, itemIndex) {
      // Clear existing timeout for this item
      if (this.searchTimeouts[itemIndex]) {
        clearTimeout(this.searchTimeouts[itemIndex])
      }

      // Set new timeout for debouncing
      this.searchTimeouts[itemIndex] = setTimeout(() => {
        if (!query || query.trim() === '') {
          // Show first 15 products when query is empty
          this.$set(this.pobItems[itemIndex], 'filteredProductOptions', this.productOptions.slice(0, 15))
        } else {
          // Filter products by name (case-insensitive)
          const searchTerm = query.toLowerCase()
          const filtered = this.productOptions.filter(product => product.name.toLowerCase().includes(searchTerm))
          // Limit to 50 results for performance
          this.$set(this.pobItems[itemIndex], 'filteredProductOptions', filtered.slice(0, 50))
        }
      }, 200) // Reduced to 200ms for faster response
    },

    handleProductChange(product, itemIndex) {
      // Clear validation error for this item using $delete
      if (this.validationErrors[`product_${itemIndex}`]) {
        this.$delete(this.validationErrors, `product_${itemIndex}`)
      }

      // Check if this product is already selected in another item
      const isDuplicate = this.pobItems.some((item, index) => {
        if (index === itemIndex) return false // Don't compare with itself
        return item.product && product
               && ((item.product.id && item.product.id === product.id)
                || (item.product.code && item.product.code === product.code))
      })

      if (isDuplicate) {
        // Show toast error for duplicate selection
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Duplicate Product',
            text: 'This product is already selected in another item',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })

        // Don't set the product if it's a duplicate
        this.$set(this.pobItems[itemIndex], 'product', null)
        this.$set(this.pobItems[itemIndex], 'rate', null)
        this.$set(this.pobItems[itemIndex], 'mrp', null)
        return
      }

      // Set the product if it's not a duplicate
      this.$set(this.pobItems[itemIndex], 'product', product)
      this.$set(this.pobItems[itemIndex], 'rate', product?.rate || 0)
      this.$set(this.pobItems[itemIndex], 'mrp', product?.mrp || 0)
      // Check for duplicates and update isDuplicate flag for all items
      this.checkForDuplicates()
    },

    handleMrFocus() {
      if (!this.selectedCompany) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Company Required',
            text: 'Please select a company first before selecting MR',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
      }
    },

    checkForDuplicates() {
      // Reset all duplicate flags
      this.pobItems.forEach((item, index) => {
        this.$set(this.pobItems[index], 'isDuplicate', false)
      })

      // Check for duplicates
      const selectedProducts = {}
      this.pobItems.forEach((item, index) => {
        if (item.product && (item.product.id || item.product.code)) {
          const productId = item.product.id || item.product.code

          if (selectedProducts[productId]) {
            // Mark both current and previous as duplicates
            this.$set(this.pobItems[index], 'isDuplicate', true)
            this.$set(this.pobItems[selectedProducts[productId]], 'isDuplicate', true)
          } else {
            selectedProducts[productId] = index
          }
        }
      })
    },

    addItem() {
      // Check if any existing item doesn't have a product selected
      const hasEmptyItems = this.pobItems.some(item => !item.product)

      if (hasEmptyItems) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Incomplete Items',
            text: 'Please select a product for all existing items before adding a new one',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      this.pobItems.push({
        id: null,
        tempId: this.generateTempId(),
        product: null,
        quantity: null,
        free_quantity: null,
        rate: null,
        mrp: null,
        filteredProductOptions: this.productOptions.slice(0, 15),
        isDuplicate: false,
      })
    },

    removeItem(index) {
      // Clear timeout if exists
      if (this.searchTimeouts[index]) {
        clearTimeout(this.searchTimeouts[index])
        delete this.searchTimeouts[index]
      }

      // Clear validation errors for this item
      if (this.validationErrors[`product_${index}`]) {
        this.$delete(this.validationErrors, `product_${index}`)
      }
      if (this.validationErrors[`quantity_${index}`]) {
        this.$delete(this.validationErrors, `quantity_${index}`)
      }

      this.pobItems.splice(index, 1)

      // Recheck for duplicates after removal
      this.checkForDuplicates()
    },

    addNewRemark() {
      this.newRemarks.push({
        id: null,
        tempId: this.generateTempId(),
        remarks: '',
      })
    },

    removeNewRemark(index) {
      this.newRemarks.splice(index, 1)
    },

    viewFile() {
      if (this.existingFileUrl) {
        window.open(this.existingFileUrl, '_blank')
      }
    },

    removeExistingFile() {
      this.existingFileUrl = null
      this.pobFile = null
    },

    submitHandler(event) {
      event.preventDefault()

      // Validate form before submitting
      if (!this.validateForm()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please fix the errors and try again',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      // Clear any previous errors
      this.errorMessage = {}
      this.saving = true

      // Create FormData with only two keys
      const formData = new FormData()

      // Prepare all data as JSON object
      const dataPayload = {}

      // Basic information
      if (this.selectedCompany) dataPayload.company = this.selectedCompany.id
      if (this.selectedMr) dataPayload.mr = this.selectedMr.map(item => item.id)
      if (this.selectedParty) dataPayload.party = this.selectedParty.id
      if (this.selectedPobMode) dataPayload.pob_mode = this.selectedPobMode.value
      if (this.placedAtDate) dataPayload.placed_at = this.placedAtDate

      // Prepare POB Items - separate existing and new items
      const validItems = this.pobItems
        .filter(item => item.product && (item.quantity > 0 || item.free_quantity > 0))
        .map(item => {
          const itemData = {
            product: String(item.product.name || item.product.display_text),
            code: String(item.product.code || item.product.id),
            quantity: parseInt(item.quantity, 10) || 0,
            free_quantity: parseInt(item.free_quantity, 10) || 0,
            approved_free_quantity: item.approved_free_quantity || 0,
            approved_quantity: item.approved_quantity || 0,
            rate: item.rate || 0,
            mrp: item.mrp || 0,
          }

          // Include ID for existing items
          if (item.id) {
            itemData.id = item.id
          }

          return itemData
        })

      if (validItems.length > 0) {
        dataPayload.pob_items = validItems
      }

      // Prepare POB Remarks - only new remarks (existing ones are read-only)
      const validNewRemarks = this.newRemarks
        .filter(remark => remark.remarks && remark.remarks.trim().length > 0)
        .map(remark => ({
          remarks: String(remark.remarks.trim()),
        }))

      if (validNewRemarks.length > 0) {
        dataPayload.pob_remarks = validNewRemarks
      }

      // Add data as JSON string
      formData.append('data', JSON.stringify(dataPayload))

      // Add file handling
      if (this.pobFile) {
        // New file selected
        formData.append('pob_file', this.pobFile)
      } else if (!this.existingFileUrl) {
        // File was removed (send empty string or null to indicate removal)
        formData.append('remove_file', 'true')
      }

      // Send request
      axios.put(`/marg/edit-pob/${this.id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: 'application/json',
        },
      })
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || 'POB Updated Successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.$emit('updated')
          this.showModal = false
          this.saving = false
        })
        .catch(error => {
          this.errorMessage = error?.response?.data?.error || 'Something went wrong'
          this.saving = false
        })
    },
  },
}
</script>

<style scoped>
.error-message {
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.error-border {
  border-color: #dc3545 !important;
  /* Remove the box-shadow to avoid extra border effect */
}

.error-border:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.total-order-value {
  padding: 8px 16px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.text-danger {
  color: #dc3545 !important;
}

.existing-remark-card {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
}

.remark-text {
  color: #495057;
  line-height: 1.5;
  word-wrap: break-word;
}
</style>
